import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { FilePdf, Crown, BookOpen, Buildings, Users, CheckCircle, XCircle, Clock, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Kpi = ({ label, value, color }) => (
  <div className={`p-3 rounded-lg border text-center ${color}`}>
    <p className="text-2xl font-bold leading-tight">{value ?? 0}</p>
    <p className="text-[10px] font-medium uppercase opacity-70 mt-0.5">{label}</p>
  </div>
);

export default function MandanteReport() {
  const [mandantes, setMandantes] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await reportsApi.mandantes();
        setMandantes(res.data || []);
        if (res.data && res.data.length) setSelectedId(res.data[0].id);
      } catch { toast.error('Error al cargar mandantes'); }
    })();
  }, []);

  const fetchReport = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await reportsApi.mandante(id, params);
      setData(res.data);
    } catch {
      toast.error('Error al generar el reporte del mandante');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (selectedId) fetchReport(selectedId);
  }, [selectedId]);

  const handleExport = async () => {
    if (!selectedId) return;
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await reportsApi.exportMandante(selectedId, params);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_gestion_${(data?.mandante_name || 'mandante').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Informe PDF descargado');
    } catch {
      toast.error('Error al exportar el informe');
    } finally {
      setExporting(false);
    }
  };

  const s = data?.summary || {};
  const evaluated = (s.aprobados || 0) + (s.reprobados || 0);
  const approval = evaluated ? Math.round((s.aprobados || 0) / evaluated * 1000) / 10 : 0;

  return (
    <div className="space-y-4" data-testid="mandante-report">
      {/* Controls */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs text-[#2B4B8C]/60">Mandante</label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="mandante-select">
                  <SelectValue placeholder="Seleccione un mandante" />
                </SelectTrigger>
                <SelectContent>
                  {mandantes.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-[#2B4B8C]/60">Desde</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="mandante-date-from" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-[#2B4B8C]/60">Hasta</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="mandante-date-to" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => fetchReport(selectedId)} className="bg-[#2B4B8C] hover:bg-[#233c72] text-white" data-testid="mandante-apply-btn">
              <ChartBar size={16} className="mr-1.5" /> Generar informe
            </Button>
            <Button onClick={handleExport} disabled={!data || exporting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="mandante-export-pdf">
              <FilePdf size={16} className="mr-1.5" /> {exporting ? 'Generando...' : 'Exportar PDF'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
        </div>
      ) : !data ? (
        <div className="py-16 text-center text-[#2B4B8C]/40 text-sm">Seleccione un mandante para generar el informe</div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center gap-2">
            <Crown size={22} className="text-[#2BBAA4]" />
            <h2 className="text-xl font-bold text-[#2B4B8C]">{data.mandante_name}</h2>
            {data.mandante_rut ? <span className="text-sm text-[#2B4B8C]/50">{data.mandante_rut}</span> : null}
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Kpi label="Capacitaciones" value={data.trainings_count} color="bg-blue-50 border-blue-200 text-blue-600" />
            <Kpi label="Cursos" value={data.courses_count} color="bg-indigo-50 border-indigo-200 text-indigo-600" />
            <Kpi label="Trabajadores" value={s.trabajadores} color="bg-purple-50 border-purple-200 text-purple-600" />
            <Kpi label="Empresas" value={s.empresas} color="bg-cyan-50 border-cyan-200 text-cyan-600" />
            <Kpi label="Inscripciones" value={s.total_registros} color="bg-slate-50 border-slate-200 text-slate-600" />
            <Kpi label="Aprobados" value={s.aprobados} color="bg-green-50 border-green-200 text-green-600" />
            <Kpi label="Reprobados" value={s.reprobados} color="bg-red-50 border-red-200 text-red-500" />
            <Kpi label="% Aprobación" value={`${approval}%`} color="bg-teal-50 border-teal-200 text-teal-600" />
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-[#2B4B8C]/60 px-1">
            <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-600" /> Cert. vigentes: <b>{s.vigentes || 0}</b></span>
            <span className="flex items-center gap-1"><XCircle size={14} className="text-red-500" /> Cert. vencidos: <b>{s.vencidos || 0}</b></span>
            <span className="flex items-center gap-1"><Clock size={14} className="text-amber-600" /> Pendientes: <b>{s.pendientes || 0}</b></span>
          </div>

          {/* By course */}
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-[#2B4B8C]/10 flex items-center gap-2">
                <BookOpen size={16} className="text-[#2BBAA4]" />
                <h3 className="font-semibold text-[#2B4B8C] text-sm">Detalle por Curso</h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Curso</TableHead>
                      <TableHead className="text-xs text-center">Vigencia</TableHead>
                      <TableHead className="text-xs text-center">Capac.</TableHead>
                      <TableHead className="text-xs text-center">Inscrip.</TableHead>
                      <TableHead className="text-xs text-center">Aprob.</TableHead>
                      <TableHead className="text-xs text-center">Reprob.</TableHead>
                      <TableHead className="text-xs text-center">Pend.</TableHead>
                      <TableHead className="text-xs text-center">% Aprob.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_course.map((c, i) => (
                      <TableRow key={i} data-testid="mandante-course-row">
                        <TableCell className="text-xs font-medium text-[#2B4B8C]">{c.course_name}</TableCell>
                        <TableCell className="text-xs text-center">
                          {c.validity_years ? (
                            <span className="inline-flex items-center rounded-full bg-teal-50 border border-teal-200 text-teal-700 px-2 py-0.5 font-medium">{c.vigencia_label}</span>
                          ) : (
                            <span className="text-[#2B4B8C]/40">Sin vigencia</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-center">{c.trainings}</TableCell>
                        <TableCell className="text-xs text-center">{c.registros}</TableCell>
                        <TableCell className="text-xs text-center text-green-600 font-semibold">{c.aprobados}</TableCell>
                        <TableCell className="text-xs text-center text-red-500 font-semibold">{c.reprobados}</TableCell>
                        <TableCell className="text-xs text-center text-amber-600">{c.pendientes}</TableCell>
                        <TableCell className="text-xs text-center font-bold text-[#2B4B8C]">{c.approval_rate_label}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* By company */}
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-[#2B4B8C]/10 flex items-center gap-2">
                <Buildings size={16} className="text-[#2BBAA4]" />
                <h3 className="font-semibold text-[#2B4B8C] text-sm">Detalle por Empresa (Contratista) — {data.by_company.length}</h3>
              </div>
              <div className="overflow-x-auto max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Empresa</TableHead>
                      <TableHead className="text-xs text-center">Trab.</TableHead>
                      <TableHead className="text-xs text-center">Inscrip.</TableHead>
                      <TableHead className="text-xs text-center">Aprob.</TableHead>
                      <TableHead className="text-xs text-center">Reprob.</TableHead>
                      <TableHead className="text-xs text-center">% Aprob.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_company.map((c, i) => (
                      <TableRow key={i} data-testid="mandante-company-row">
                        <TableCell className="text-xs font-medium text-[#2B4B8C]">{c.company_name}</TableCell>
                        <TableCell className="text-xs text-center">{c.workers_count}</TableCell>
                        <TableCell className="text-xs text-center">{c.registros}</TableCell>
                        <TableCell className="text-xs text-center text-green-600 font-semibold">{c.aprobados}</TableCell>
                        <TableCell className="text-xs text-center text-red-500 font-semibold">{c.reprobados}</TableCell>
                        <TableCell className="text-xs text-center font-bold text-[#2B4B8C]">{c.approval_rate_label}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
